import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Grid,

  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  PlayArrow as ProcessIcon,
  CheckCircle as CheckIcon,

  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../utils/apiClient';

const ProcessingConfirmation = ({ 
  workflowData, 
  onDataUpdate, 
  onNext, 
  onBack, 
  isValid, 
  isProcessing, 
  setIsProcessing 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Local state
  const [finalValidation, setFinalValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [acknowledgeRisks, setAcknowledgeRisks] = useState(false);

  // Per-school AI summary status: { [uuid]: 'generating' | 'complete' | 'timeout' }
  const [aiSummaryStatuses, setAiSummaryStatuses] = useState({});

  // Poll status for every split school until all have summaries or 10 minutes elapse
  useEffect(() => {
    const splits = processingResults?.processed_splits;
    if (!splits?.length) return;

    const uuids = splits.map(s => s.uuid).filter(Boolean);
    if (!uuids.length) return;

    const initialStatuses = {};
    uuids.forEach(uuid => { initialStatuses[uuid] = 'generating'; });
    setAiSummaryStatuses(initialStatuses);

    const pendingUuids = new Set(uuids);

    const checkStatuses = async () => {
      if (pendingUuids.size === 0) return;
      for (const uuid of [...pendingUuids]) {
        try {
          const status = await apiClient.get(`/v2/schools/${uuid}/ai-summary/status`);
          if (status?.has_any_summaries) {
            pendingUuids.delete(uuid);
            setAiSummaryStatuses(prev => ({ ...prev, [uuid]: 'complete' }));
          }
        } catch (err) {
          console.warn(`AI summary status check failed for ${uuid}:`, err);
        }
      }
      if (pendingUuids.size === 0) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      }
    };

    const intervalId = setInterval(checkStatuses, 15000);
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      if (pendingUuids.size > 0) {
        setAiSummaryStatuses(prev => {
          const updated = { ...prev };
          pendingUuids.forEach(uuid => { updated[uuid] = 'timeout'; });
          return updated;
        });
      }
    }, 600000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [processingResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Run final validation when component loads
  useEffect(() => {
    runFinalValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Run final validation
  const runFinalValidation = async () => {
    setIsValidating(true);
    
    try {
      // Build addresses object for validation
      const addresses = {};
      workflowData.splits.forEach((split, index) => {
        if (split.address) {
          addresses[split.suffix] = {
            address: `${split.address.street}, ${split.address.city}, ${split.address.state} ${split.address.zip}`,
            street: split.address.street,
            city: split.address.city,
            state: split.address.state,
            zip: split.address.zip
          };
        }
      });

      // Build split config object
      const splitConfig = workflowData.splits.reduce((config, split) => {
        config[split.suffix] = {
          displayName: split.displayName,
          gradesServed: split.gradesServed,
          splitType: split.splitType,
          suffix: split.suffix
        };
        return config;
      }, {});

      const response = await apiClient.post('/v2/splits/test', {
        identifier: workflowData.parentNcessch,
        split_config: splitConfig,
        addresses: addresses
      });

      setFinalValidation(response);
      onDataUpdate({ validationResults: response });
      
      if (response.validation?.is_valid) {
        enqueueSnackbar('Final validation passed - ready to process', { variant: 'success' });
      } else {
        enqueueSnackbar('Validation issues found - please review', { variant: 'warning' });
      }
      
    } catch (error) {
      console.error('Error running final validation:', error);
      enqueueSnackbar('Error running final validation', { variant: 'error' });
      setFinalValidation({
        validation: {
          is_valid: false,
          errors: ['Failed to run final validation']
        }
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Execute the split processing
  const executeSplitProcessing = async () => {
    setIsProcessing(true);
    setProcessingStatus({ step: 'starting', message: 'Initializing split processing...' });

    try {
      // Build the processing request
      const processingRequest = {
        splits: workflowData.splits.map(split => ({
          displayName: split.displayName,
          suffix: split.suffix,
          gradesServed: split.gradesServed,
          splitType: split.splitType,
          address: split.address
        })),
        teamAssignments: workflowData.teamAssignments
      };

      setProcessingStatus({ step: 'creating', message: 'Creating split schools...' });

      // Execute the split creation
      const response = await apiClient.post(
        `/v2/splits/${workflowData.parentNcessch}/config`,
        processingRequest
      );

      if (response.success) {
        setProcessingStatus({ step: 'processing', message: 'Running standard school processing...' });
        
        // The backend will handle the processing pipeline
        setProcessingResults(response);
        setProcessingStatus({ step: 'completed', message: 'Split processing completed successfully!' });
        
        enqueueSnackbar('Split school processing completed successfully', { variant: 'success' });
      } else {
        throw new Error(response.error || 'Split processing failed');
      }

    } catch (error) {
      console.error('Error executing split processing:', error);
      setProcessingStatus({ 
        step: 'failed', 
        message: error.message || 'Split processing failed'
      });
      enqueueSnackbar('Split processing failed', { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate totals for summary
  const calculateSummaryTotals = () => {
    const totalGrades = workflowData.splits.reduce((total, split) => {
      return total + split.gradesServed.length;
    }, 0);

    const totalEnrollment = workflowData.splits.reduce((total, split) => {
      if (!workflowData.parentSchool?.enrollmentData?.by_grade) return total;
      
      return total + split.gradesServed.reduce((splitTotal, grade) => {
        const gradeData = workflowData.parentSchool.enrollmentData.by_grade[grade];
        return splitTotal + (gradeData?.total || 0);
      }, 0);
    }, 0);

    return { totalGrades, totalEnrollment };
  };

  const { totalGrades, totalEnrollment } = calculateSummaryTotals();

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ProcessIcon color="primary" />
        Final Review & Processing
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review your split school configuration and execute the processing workflow.
      </Typography>

      {/* Processing Status */}
      {isProcessing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">
                Processing Split Schools...
              </Typography>
            </Box>
            
            {processingStatus && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {processingStatus.message}
                </Typography>
                <LinearProgress 
                  variant={processingStatus.step === 'completed' ? 'determinate' : 'indeterminate'}
                  value={processingStatus.step === 'completed' ? 100 : undefined}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Processing Results */}
      {processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" />
              Processing Completed Successfully
            </Typography>
            
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Split Schools Created</AlertTitle>
              {processingResults.processed_splits?.length || workflowData.splits.length} split schools 
              have been created and are being processed through the standard workflow.
            </Alert>

            {processingResults.processed_splits && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>School Name</strong></TableCell>
                      <TableCell><strong>UUID</strong></TableCell>
                      <TableCell><strong>Created</strong></TableCell>
                      <TableCell><strong>AI Summaries</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {processingResults.processed_splits.map((split, index) => {
                      const summaryStatus = aiSummaryStatuses[split.uuid];
                      return (
                        <TableRow key={index}>
                          <TableCell>{split.name}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {split.uuid}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Created" color="success" size="small" />
                          </TableCell>
                          <TableCell>
                            {summaryStatus === 'generating' && (
                              <Chip
                                icon={<CircularProgress size={12} sx={{ color: 'inherit !important' }} />}
                                label="Generating…"
                                size="small"
                                variant="outlined"
                                color="info"
                              />
                            )}
                            {summaryStatus === 'complete' && (
                              <Chip label="Ready" color="success" size="small" />
                            )}
                            {summaryStatus === 'timeout' && (
                              <Chip label="Check back soon" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Summary */}
      {!processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Split Configuration Summary
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Parent School
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {workflowData.parentSchool?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>NCESSCH:</strong> {workflowData.parentNcessch}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Summary Totals
                </Typography>
                <Typography variant="body2">
                  <strong>Split Schools:</strong> {workflowData.splits?.length || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Grades:</strong> {totalGrades}
                </Typography>
                <Typography variant="body2">
                  <strong>Est. Total Enrollment:</strong> {totalEnrollment}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Split Schools Detail */}
      {!processingResults && workflowData.splits && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Split Schools Configuration
            </Typography>
            
            {workflowData.splits.map((split, index) => (
              <Accordion key={split.id || index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="subtitle1">
                      {split.displayName}
                    </Typography>
                    <Chip 
                      label={`Grades: ${split.gradesServed?.join(', ')}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`-${split.suffix}`} 
                      size="small" 
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body2">
                        {split.address?.street}
                      </Typography>
                      <Typography variant="body2">
                        {split.address?.city}, {split.address?.state} {split.address?.zip}
                      </Typography>
                      {split.address?.formatted_address && (
                        <Typography variant="caption" color="text.secondary">
                          Validated: {split.address.formatted_address}
                        </Typography>
                      )}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Configuration
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {split.splitType}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Grades:</strong> {split.gradesServed?.join(', ')}
                      </Typography>
                      {workflowData.parentSchool?.enrollmentData && (
                        <Typography variant="body2">
                          <strong>Est. Enrollment:</strong> {split.gradesServed?.reduce((total, grade) => {
                            const gradeData = workflowData.parentSchool.enrollmentData.by_grade?.[grade];
                            return total + (gradeData?.total || 0);
                          }, 0)}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Final Validation Results */}
      {!processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Final Validation
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={runFinalValidation}
                disabled={isValidating}
                startIcon={isValidating ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {isValidating ? 'Validating...' : 'Revalidate'}
              </Button>
            </Box>

            {isValidating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Running final validation...</Typography>
              </Box>
            ) : finalValidation ? (
              <Box>
                {finalValidation.validation?.is_valid ? (
                  <Alert severity="success">
                    <AlertTitle>All Validations Passed</AlertTitle>
                    Configuration is valid and ready for processing.
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <AlertTitle>Validation Issues Found</AlertTitle>
                    {finalValidation.validation?.errors?.map((error, index) => (
                      <Typography key={index} variant="body2">
                        • {error}
                      </Typography>
                    ))}
                    {finalValidation.validation?.warnings?.map((warning, index) => (
                      <Typography key={index} variant="body2" color="warning.main">
                        ⚠ {warning}
                      </Typography>
                    ))}
                  </Alert>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                <AlertTitle>Validation Required</AlertTitle>
                Click "Revalidate" to run final validation checks.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Processing Actions */}
      {!processingResults && !isProcessing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Execute Split Processing
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Important Notice</AlertTitle>
              <Typography variant="body2" gutterBottom>
                This action will:
              </Typography>
              <ul>
                <li>Create {workflowData.splits?.length} new school records with unique UUIDs</li>
                <li>Copy and split enrollment and FRL data from the parent school</li>
                <li>Set the parent school status to inactive</li>
                <li>Process each split school through ESRI, Projections, Metrics, and Nearby Schools</li>
                <li>Assign teams according to your configuration</li>
              </ul>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>This action cannot be easily undone.</strong>
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgeRisks}
                  onChange={(e) => setAcknowledgeRisks(e.target.checked)}
                />
              }
              label="I understand this action will modify the database and cannot be easily reversed"
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={onBack}
                disabled={isProcessing}
              >
                Back to Review
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => setConfirmationDialog(true)}
                disabled={
                  !finalValidation?.validation?.is_valid || 
                  !acknowledgeRisks || 
                  isProcessing
                }
                startIcon={<ProcessIcon />}
              >
                Execute Split Processing
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmationDialog} onClose={() => setConfirmationDialog(false)}>
        <DialogTitle>Confirm Split Processing</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to execute the split processing for:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
            {workflowData.parentSchool?.name} ({workflowData.parentNcessch})
          </Typography>
          <Typography variant="body2">
            This will create {workflowData.splits?.length} new split schools and cannot be easily undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setConfirmationDialog(false);
              executeSplitProcessing();
            }}
            variant="contained"
            color="primary"
          >
            Confirm and Process
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessingConfirmation;
